// « Départs de demain » — the pure rules behind the sheet and the prefill
// (PMS-TERMINAL-LINK-BRIEF §8.8). No React, no I/O: tested on their own.

import type {
  ArrivalBill,
  DayPayment,
  DayTakingsLine,
  DepartureBill,
  DeskShift,
  IsoDate,
  OtherTaking,
  PaymentLine,
  ReceptionPaymentId,
  TakingsBucket,
  TenderType,
} from '@lemontaigu/pms-core';

/** The hotel's wall clock, whatever the machine's timezone. */
function parisParts(now: Date): { date: IsoDate; hour: number } {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Paris',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(now);
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? '';
  return { date: `${get('year')}-${get('month')}-${get('day')}` as IsoDate, hour: Number(get('hour')) };
}

function addDays(iso: IsoDate, days: number): IsoDate {
  const d = new Date(`${iso}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10) as IsoDate;
}

/** The day before / after — the day bar's ‹ and › (noon UTC dodges DST edges). */
export const shiftDay = addDays;

/** '2026-09-26' → 'samedi 26 septembre', as the board's day bar prints it. */
export function fmtDayLabel(iso: IsoDate): string {
  return new Date(`${iso}T12:00:00Z`).toLocaleDateString('fr-FR', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    timeZone: 'Europe/Paris',
  });
}

/** The evening routine prepares TOMORROW; opened in the morning (the evening
 *  run was missed), the same sheet serves TODAY. The switch is 17:00, Paris. */
export const DEPARTURE_EVENING_HOUR = 17;

export function defaultDepartureDate(now: Date): IsoDate {
  const { date, hour } = parisParts(now);
  return hour >= DEPARTURE_EVENING_HOUR ? addDays(date, 1) : date;
}

/** Today on the hotel's clock — the « Arrivées » sheet always opens on it. */
export function todayParis(now: Date): IsoDate {
  return parisParts(now).date;
}

/** '0214' → '214' — the number on the door, which is what the desk types. */
export function displayRoom(room: string): string {
  return /^\d+$/.test(room) ? String(Number(room)) : room;
}

/** From this many rooms a booking's list is shown as a count and by floor
 *  (§8.8o, operator 2026-09-26) — AMIHAN's 30 rooms took six lines. */
export const ROOMS_BY_FLOOR_FROM = 6;

/** A long room list, one entry per floor, a run of three or more consecutive
 *  doors as a range: 202, 203 … 210, 212, 214 → « 202–210, 212, 214 ». A room
 *  whose number is not three digits goes on a line of its own, under ''. */
export function roomsByFloor(rooms: string[]): { floor: string; text: string }[] {
  const doors = [...new Set(rooms.map(displayRoom))];
  const floors = new Map<string, string[]>();
  for (const d of doors) {
    const floor = /^\d{3}$/.test(d) ? d[0]! : '';
    floors.set(floor, [...(floors.get(floor) ?? []), d]);
  }
  return [...floors.entries()]
    .sort(([a], [b]) => (a === '' ? 1 : b === '' ? -1 : a.localeCompare(b)))
    .map(([floor, list]) => {
      const nums = list
        .filter((d) => /^\d+$/.test(d))
        .map(Number)
        .sort((x, y) => x - y);
      const parts: string[] = [];
      for (let i = 0; i < nums.length; ) {
        let j = i;
        while (j + 1 < nums.length && nums[j + 1] === nums[j]! + 1) j++;
        if (j - i >= 2) parts.push(`${nums[i]}–${nums[j]}`);
        else for (let k = i; k <= j; k++) parts.push(String(nums[k]));
        i = j + 1;
      }
      parts.push(...list.filter((d) => !/^\d+$/.test(d)).sort());
      return { floor, text: parts.join(', ') };
    });
}

/** Bill so far minus what the guest already paid: the arrhes Vega holds and
 *  what the log collected (operator 2026-09-25 — « that's the amount to
 *  encaisser »). Negative is an overpayment and is shown as such, never
 *  clamped. NOT subtracted: « en attente » (owed and deferred, not money in)
 *  and unattributed arrhes (a guest with several bookings — shown « à
 *  vérifier », never guessed).
 *
 *  A guest who has LEFT is owed Vega's pending notes, which already absorbed
 *  the arrhes applied to them — so only the log is netted there (§8.8h).
 *  Arrhes Vega still holds after a departure are shown, never subtracted. */
export function toCollectCents(b: DepartureBill): number {
  if (b.checkedOut) return b.billCents - b.collectedCents;
  return b.billCents - b.depositCents - b.collectedCents;
}

/** What the lines left off every note would have come to — information only. */
export function unbilledCents(b: DepartureBill): number {
  return b.unbilledLines.reduce((t, l) => t + l.cents, 0);
}

/** The feed checks Vega every minute: a check older than this means it is not
 *  running (or Vega's server is off), and the sheet says so. */
export const VEGA_READ_STALE_MINUTES = 5;

/** « Vega relu à 14:03 » — when the feed last confirmed the mirror against Vega
 *  (§8.8h) — with its age once it is older than a few ticks. Null when unknown. */
export function vegaReadLabel(readAt: string | null, now: Date): { text: string; stale: boolean } | null {
  if (!readAt) return null;
  const at = new Date(readAt);
  if (Number.isNaN(at.getTime())) return null;
  const time = at.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Paris' });
  const minutes = Math.floor((now.getTime() - at.getTime()) / 60_000);
  if (minutes <= VEGA_READ_STALE_MINUTES) return { text: `Vega relu à ${time}`, stale: false };
  const age = minutes < 60 ? `${minutes} min` : `${Math.floor(minutes / 60)} h ${String(minutes % 60).padStart(2, '0')}`;
  return { text: `Vega relu à ${time}, il y a ${age}`, stale: true };
}

/** 2 → '2', 1.5 → '1,5' — a quantity as the desk writes it. */
export function fmtQty(q: number): string {
  return Number.isInteger(q) ? String(q) : String(q).replace('.', ',');
}

/** Cents as the amount field expects them: '342,48'. */
export function centsToInput(cents: number): string {
  return (cents / 100).toFixed(2).replace('.', ',');
}

/** The first room a lookup can resolve — a group resolves from any of its rooms. */
export function roomToBill(b: DepartureBill): string | null {
  const room = b.rooms.find((r) => /^\d+$/.test(r)) ?? b.rooms[0];
  return room ? displayRoom(room) : null;
}

/** What « Encaisser » types into « Chambre ou résa » (§8.8j): a group's own
 *  account is the whole booking, so its résa number; a room's account, that
 *  room; a booking on a single account, its first room, as before. */
export function billTarget(b: DepartureBill): string | null {
  if (b.unit === 'GRP') return b.bookingRef;
  if (b.unit) return displayRoom(b.unit);
  return roomToBill(b);
}

/** Who the row is for: the room's guest on a room's account, the party otherwise. */
export function displayName(b: DepartureBill): string {
  return b.unit && b.unit !== 'GRP' && b.unitLabel ? b.unitLabel : b.partyLabel;
}

// ── Sorting each section (operator 2026-09-25) ──────────────────────────────

export type DepartureSort = 'room' | 'name' | 'paid';
export type ArrivalSort = 'name' | 'room' | 'time';

const roomNumber = (rooms: string[]): number => {
  const n = rooms.map((r) => Number(r)).filter((x) => Number.isFinite(x));
  return n.length ? Math.min(...n) : Number.POSITIVE_INFINITY;
};
const byName = (a: string, b: string) => a.localeCompare(b, 'fr', { sensitivity: 'base' });

/** « Chambre » keeps a group's rows together — its own account first, then its
 *  rooms — at its lowest room; « Nom » by who the row is for; « Heure de
 *  règlement » by the last payment, LATEST first (operator 2026-09-26), rows
 *  never paid last. */
export function sortDepartures(rows: DepartureBill[], key: DepartureSort): DepartureBill[] {
  const bookingRoom = new Map<string, number>();
  for (const b of rows) {
    bookingRoom.set(b.bookingRef, Math.min(bookingRoom.get(b.bookingRef) ?? Infinity, roomNumber(b.rooms)));
  }
  const room = (a: DepartureBill, b: DepartureBill) =>
    (bookingRoom.get(a.bookingRef)! - bookingRoom.get(b.bookingRef)!) ||
    a.bookingRef.localeCompare(b.bookingRef) ||
    Number(b.isMain) - Number(a.isMain) ||
    roomNumber(a.rooms) - roomNumber(b.rooms);
  const paid = (a: DepartureBill, b: DepartureBill) => {
    if (a.lastPaidAt === b.lastPaidAt) return 0;
    if (!a.lastPaidAt) return 1;
    if (!b.lastPaidAt) return -1;
    return a.lastPaidAt > b.lastPaidAt ? -1 : 1;
  };
  const cmp =
    key === 'name'
      ? (a: DepartureBill, b: DepartureBill) => byName(displayName(a), displayName(b)) || room(a, b)
      : key === 'paid'
        ? (a: DepartureBill, b: DepartureBill) => paid(a, b) || room(a, b)
        : room;
  return [...rows].sort(cmp);
}

/** « Nom », « Chambre », or « Heure d'arrivée » (the expected time before
 *  check-in, the real one after), times unknown last. `latestFirst` for a time
 *  that has HAPPENED — the arrived (operator 2026-09-26: latest on top); an
 *  expected time keeps the next one first. */
export function sortArrivals(rows: ArrivalBill[], key: ArrivalSort, latestFirst = false): ArrivalBill[] {
  const room = (a: ArrivalBill, b: ArrivalBill) =>
    roomNumber(a.rooms) - roomNumber(b.rooms) || a.bookingRef.localeCompare(b.bookingRef);
  const time = (a: ArrivalBill, b: ArrivalBill) => {
    if (a.arrivalTime === b.arrivalTime) return 0;
    if (!a.arrivalTime) return 1;
    if (!b.arrivalTime) return -1;
    return (a.arrivalTime < b.arrivalTime) !== latestFirst ? -1 : 1;
  };
  const cmp =
    key === 'name'
      ? (a: ArrivalBill, b: ArrivalBill) => byName(a.partyLabel, b.partyLabel) || room(a, b)
      : key === 'time'
        ? (a: ArrivalBill, b: ArrivalBill) => time(a, b) || room(a, b)
        : room;
  return [...rows].sort(cmp);
}

/**
 * A split payment (several cards, card + ANCV, cash…): what is left after one
 * payment of `paidCents` against a remainder. Null means the target is settled
 * and the form can clear; a positive number is prefilled for the next tender.
 */
export function remainderAfter(remainingCents: number, paidCents: number): number | null {
  const left = remainingCents - paidCents;
  return left > 0 ? left : null;
}

// ── Two sections and the payment list (operator 2026-09-25) ─────────────────

/** Still something to collect on top, settled below — and, third (§8.8l,
 *  operator 2026-09-26), a guest who left with notes put on a DEBTOR account:
 *  nothing left to collect at the desk, but not paid either (CADAY, 26/09).
 *  An overpayment (« payé > note ») is settled and keeps its flag. A row with
 *  something to collect stays on top even if part of it went on account. */
export function splitBySettlement(rows: DepartureBill[]): {
  toCollect: DepartureBill[];
  settled: DepartureBill[];
  debtors: DepartureBill[];
} {
  const due = (b: DepartureBill) => toCollectCents(b) > 0;
  return {
    toCollect: rows.filter(due),
    settled: rows.filter((b) => !due(b) && b.debts.length === 0),
    debtors: rows.filter((b) => !due(b) && b.debts.length > 0),
  };
}

/** What the booking put on account, and what is still owed on it. */
export function transferredCents(b: DepartureBill): number {
  return b.debts.reduce((t, d) => t + d.cents, 0);
}

/** Everything paid on the booking, whatever the channel. Signed. */
export function paidCents(payments: PaymentLine[]): number {
  return payments.reduce((t, p) => t + p.cents, 0);
}

/** The date of the most recent payment, or null when none carries a date. */
export function lastPaymentDate(payments: PaymentLine[]): IsoDate | null {
  return payments.reduce<IsoDate | null>((last, p) => (p.date && (!last || p.date > last) ? p.date : last), null);
}

/** '2026-09-21' → '21/09'. */
export function dayMonth(iso: string | null): string {
  return iso && iso.length >= 10 ? `${iso.slice(8, 10)}/${iso.slice(5, 7)}` : '—';
}

/** A payment's stamp on the sheets: « 24/09 16:37 » when we know when it was
 *  keyed (the hotel's clock), else Vega's date alone. */
export function paymentStamp(p: PaymentLine): string {
  if (!p.at) return dayMonth(p.date);
  const at = new Date(p.at);
  if (Number.isNaN(at.getTime())) return dayMonth(p.date);
  const parts = new Intl.DateTimeFormat('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
    timeZone: 'Europe/Paris',
  }).formatToParts(at);
  const get = (t: string) => parts.find((x) => x.type === t)?.value ?? '';
  return `${get('day')}/${get('month')} ${get('hour')}:${get('minute')}`;
}

/** Vega's 'HHMM' → '15:21'. */
export function hhmm(t: string | null): string | null {
  return t && /^\d{4}$/.test(t) ? `${t.slice(0, 2)}:${t.slice(2)}` : null;
}

/** Nights between two ISO dates. */
export function nightsBetween(arrival: string, departure: string): number {
  const ms = new Date(`${departure}T12:00:00Z`).getTime() - new Date(`${arrival}T12:00:00Z`).getTime();
  return Math.max(Math.round(ms / 86_400_000), 0);
}

// ── Arrivées takes the day's payments (§8.8k, operator 2026-09-26) ──────────

/** What is left of the stay to collect: the stay less EVERYTHING paid on the
 *  booking — Vega's arrhes and règlements, and what the form took that Vega
 *  does not show yet (all of them on the payment list). Negative = paid more
 *  than the plan's stay, shown « à vérifier », never clamped.
 *
 *  NOT the arrhes Vega still holds (GARRHES): the evening's note preparation
 *  consumes the arrhes of every note they cover, so at 23:07 on 2026-09-25
 *  that register was empty for 9 of the 16 guests who had paid their whole
 *  stay at check-in — each would have read « à encaisser » for all of it. */
export function arrivalToCollectCents(a: ArrivalBill): number {
  return a.stayCents - paidCents(a.payments);
}

/** The day a payment belongs to: Vega's own date for a Vega line (its
 *  business date — never the keyed time, which is the record's LAST write and
 *  would move an old deposit into today when it is edited), the capture day
 *  for one taken in the form. */
export function paymentDay(p: PaymentLine): IsoDate | null {
  return p.date ?? (p.at ? todayParis(new Date(p.at)) : null);
}

/** A booking's payments against a sheet's day: paid before it, on it, after
 *  it (§8.8o). A payment with no date at all counts as already paid (before). */
export function splitByDay(
  payments: PaymentLine[],
  day: IsoDate,
): { before: PaymentLine[]; onDay: PaymentLine[]; after: PaymentLine[] } {
  const before: PaymentLine[] = [];
  const onDay: PaymentLine[] = [];
  const after: PaymentLine[] = [];
  for (const p of payments) {
    const d = paymentDay(p);
    (d === null || d < day ? before : d === day ? onDay : after).push(p);
  }
  return { before, onDay, after };
}

/** An arrival as the sheet of `day` reads it (§8.8o, operator 2026-09-26):
 *  « Arrhes » — everything paid before the day; « Réglé le jour » — paid that
 *  day, Vega and the form alike; and what was left to collect at the end of
 *  it. A payment made AFTER the day belongs to a later day and is not on this
 *  sheet: yesterday's arrivals read as they stood yesterday evening. */
export function arrivalOnDay(
  a: ArrivalBill,
  day: IsoDate,
): { before: PaymentLine[]; onDay: PaymentLine[]; beforeCents: number; onDayCents: number; dueCents: number } {
  const { before, onDay } = splitByDay(a.payments, day);
  const beforeCents = paidCents(before);
  const onDayCents = paidCents(onDay);
  return { before, onDay, beforeCents, onDayCents, dueCents: a.stayCents - beforeCents - onDayCents };
}

/** What « Encaisser » types for an arrival: its room — or, for a booking of
 *  several rooms, the résa, since the stay is paid whole on the main account. */
export function arrivalTarget(a: ArrivalBill): string {
  const only = a.rooms.length === 1 ? a.rooms[0] : undefined;
  return only ? displayRoom(only) : a.bookingRef;
}

/** The form's lookup resolves a stay only from its arrival through its
 *  departure day, so « Encaisser » is offered on those days alone. */
export function staysOn(a: { arrivalDate: IsoDate; departureDate: IsoDate }, day: IsoDate): boolean {
  return a.arrivalDate <= day && day <= a.departureDate;
}

/** A résa number (six digits) or a room (« 110 »). */
export const isResaTarget = (target: string): boolean => /^\d{5,}$/.test(target);

/** « résa 022180 » for a booking number, « ch. 110 » for a room. */
export function targetLabel(target: string): string {
  return isResaTarget(target) ? `résa ${target}` : `ch. ${target}`;
}

/** An open « en attente » as the form needs it to settle one: its id, what
 *  the guest should pay, and what to type in « Chambre ou résa ». */
export interface SuspenseToSettle {
  id: ReceptionPaymentId;
  cents: number;
  target: string;
}

export const TENDER_LABEL_FR: Record<TenderType, string> = {
  card: 'Carte',
  cash: 'Espèces',
  cheque: 'Chèque',
  ancv: 'ANCV',
  transfer: 'Virement',
  vad: 'VAD',
  suspense: 'En attente',
  void: 'Annulation',
};

/** Money in — every tender but an « en attente » (deferred) and an annulation
 *  (the database's pms_reception_tender_is_money). */
export const isMoneyTender = (t: TenderType): boolean => t !== 'suspense' && t !== 'void';

/** The day's form payments that neither of that day's sheets shows — a guest
 *  mid-stay — with an « en attente » only while it is open. */
export function otherDayPayments(rows: DayPayment[]): DayPayment[] {
  return rows.filter(
    (p) => !p.arrives && !p.departs && (isMoneyTender(p.tenderType) || (p.tenderType === 'suspense' && !p.closed)),
  );
}

/** An instant as the hotel's clock reads it: « 21:58 ». */
export function timeParis(iso: string): string {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return '—';
  return new Intl.DateTimeFormat('fr-FR', {
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
    timeZone: 'Europe/Paris',
  }).format(at);
}

/** « (033276) - 024608 » — after a guest's name, the booking's Vega client
 *  number and the résa (operator 2026-09-26: « FORMATION (033276) - 024608 »). */
export function clientTag(clientRef: string | null, bookingRef: string): string {
  return clientRef ? `(${clientRef}) - ${bookingRef}` : `- ${bookingRef}`;
}

// ── « Encaissements du jour » (§8.8o) ───────────────────────────────────────

/** The band's tiles, in the order the desk reads them: what sits in the
 *  drawer and has to be counted and banked, then the electronic tenders —
 *  card apart by the statement each reconciles against (operator 2026-09-26). */
export const TAKINGS_TILES: { bucket: Exclude<TakingsBucket, 'outside'>; label: string; drawer: boolean }[] = [
  { bucket: 'cash', label: 'Espèces', drawer: true },
  { bucket: 'cheque', label: 'Chèques', drawer: true },
  { bucket: 'ancv', label: 'ANCV', drawer: true },
  { bucket: 'card', label: 'CB', drawer: false },
  { bucket: 'amex', label: 'AMEX', drawer: false },
  { bucket: 'vad', label: 'VAD', drawer: false },
  { bucket: 'transfer', label: 'Virement', drawer: false },
];

/** A band line: the RPC's (`DayTakingsLine`) or a page's own (pageTakings). */
export type TakingsLine = Omit<DayTakingsLine, 'propertyCode'>;

export interface TakingsTile {
  bucket: Exclude<TakingsBucket, 'outside'>;
  label: string;
  drawer: boolean;
  /** In less refunds. */
  netCents: number;
  /** Refunds, ≤ 0. */
  outCents: number;
  movements: number;
  notInVegaCents: number;
  notInVega: number;
}

/** The band's figures: one tile per tender (every property the desk serves
 *  together — one drawer), the day's total, and the Vega movements that are
 *  not money in a known tender, named, never added to the total. */
export function takingsSummary(lines: TakingsLine[]): {
  tiles: TakingsTile[];
  totalCents: number;
  movements: number;
  notInVega: number;
  outside: { label: string; cents: number; movements: number }[];
} {
  const tiles = TAKINGS_TILES.map((t) => {
    const mine = lines.filter((l) => l.bucket === t.bucket);
    const sum = (f: (l: TakingsLine) => number) => mine.reduce((n, l) => n + f(l), 0);
    return {
      ...t,
      netCents: sum((l) => l.inCents + l.outCents),
      outCents: sum((l) => l.outCents),
      movements: sum((l) => l.movements),
      notInVegaCents: sum((l) => l.notInVegaCents),
      notInVega: sum((l) => l.notInVega),
    };
  });
  const outside = new Map<string, { label: string; cents: number; movements: number }>();
  for (const l of lines.filter((x) => x.bucket === 'outside')) {
    const label = l.label ?? 'Autre';
    const o = outside.get(label) ?? { label, cents: 0, movements: 0 };
    outside.set(label, { label, cents: o.cents + l.inCents + l.outCents, movements: o.movements + l.movements });
  }
  return {
    tiles,
    totalCents: tiles.reduce((n, t) => n + t.netCents, 0),
    movements: tiles.reduce((n, t) => n + t.movements, 0),
    notInVega: tiles.reduce((n, t) => n + t.notInVega, 0),
    outside: [...outside.values()],
  };
}


/** Which tile a payment on the sheets counts in — to light it when its tile is
 *  picked. Vega's method code, or the form's tender (its card goes under CB:
 *  the list does not carry the terminal's scheme). Null: none. */
export function paymentBucket(p: PaymentLine): Exclude<TakingsBucket, 'outside'> | null {
  switch (p.method) {
    case '001':
    case '070':
    case 'cash':
      return 'cash';
    case '002':
    case 'cheque':
      return 'cheque';
    case '006':
    case 'ancv':
      return 'ancv';
    case '010':
    case 'card':
      return 'card';
    case '011':
      return 'amex';
    case '012':
    case 'vad':
      return 'vad';
    case '090':
    case 'transfer':
      return 'transfer';
    default:
      return null;
  }
}

/** Where a payment on a sheet counts on the band: its tender's tile; `outside`
 *  for a movement that is not money in a known tender (080, 099…); null for
 *  one that is not money at all (000, 081, 085, 997, 998) — the same rule as
 *  pms_day_takings (20260926070100). */
export function lineBucket(p: PaymentLine): TakingsBucket | null {
  const tile = paymentBucket(p);
  if (tile) return tile;
  return ['000', '081', '085', '997', '998'].includes(p.method ?? '') ? null : 'outside';
}

/** A page's band (operator 2026-09-27: « the totals are those collected on the
 *  page we see »): the given payments — a sheet's payments OF ITS DAY — by
 *  tile, refunds apart, with what Vega does not show yet. Départs is the
 *  check-out shift, Arrivées the check-in one; the day's register adds them. */
export function pageTakings(payments: PaymentLine[]): TakingsLine[] {
  const lines = new Map<string, TakingsLine>();
  for (const p of payments) {
    const bucket = lineBucket(p);
    if (!bucket) continue;
    const label = bucket === 'outside' ? (p.label ?? p.method ?? 'Autre') : null;
    const key = `${bucket}|${label ?? ''}`;
    const l = lines.get(key) ?? {
      bucket,
      label,
      inCents: 0,
      outCents: 0,
      movements: 0,
      notInVegaCents: 0,
      notInVega: 0,
    };
    const pending = p.source === 'encaissement';
    lines.set(key, {
      ...l,
      inCents: l.inCents + Math.max(p.cents, 0),
      outCents: l.outCents + Math.min(p.cents, 0),
      movements: l.movements + 1,
      notInVegaCents: l.notInVegaCents + (pending ? p.cents : 0),
      notInVega: l.notInVega + (pending ? 1 : 0),
    });
  }
  return [...lines.values()];
}

// ── « Autres encaissements » and the day's register (§8.8p) ─────────────────

/** A shift's « Autres » as band lines, to add to its page's own (§8.8p: the
 *  band is the shift's total). One line per payment; the summary adds them. */
export function othersTakings(others: OtherTaking[], shift: DeskShift): TakingsLine[] {
  return others
    .filter((o) => o.shift === shift)
    .map((o) => ({
      bucket: o.bucket,
      label: o.bucket === 'outside' ? (o.label ?? 'Autre') : null,
      inCents: Math.max(o.cents, 0),
      outCents: Math.min(o.cents, 0),
      movements: 1,
      notInVegaCents: o.source === 'form' ? o.cents : 0,
      notInVega: o.source === 'form' ? 1 : 0,
    }));
}

/** One établissement's register (U3, M11′ item 2): its own departures,
 *  arrivals, « autres » and Vega lines — never another building's. A figure is
 *  one établissement's; the register is that figure. */
export interface RegisterBlock<P> {
  property: P;
  checkout: TakingsLine[];
  checkin: TakingsLine[];
  vega: DayTakingsLine[] | null;
  /** « autres » counted on each shift, the tender ones only (as the register reads them). */
  othersOut: number;
  othersIn: number;
}

export function registerBlocks<P extends { code: string }>(
  properties: readonly P[],
  dep: readonly { b: DepartureBill; on: PaymentLine[] }[],
  arr: readonly { a: ArrivalBill; m: { onDay: PaymentLine[] } }[],
  others: readonly OtherTaking[],
  vega: readonly DayTakingsLine[] | null,
): RegisterBlock<P>[] {
  return properties.map((property) => {
    const mine = (code: string) => code === property.code;
    const theirOthers = others.filter((o) => mine(o.propertyCode));
    return {
      property,
      checkout: [
        ...pageTakings(dep.filter((x) => mine(x.b.propertyCode)).flatMap((x) => x.on)),
        ...othersTakings(theirOthers, 'checkout'),
      ],
      checkin: [
        ...pageTakings(arr.filter((x) => mine(x.a.propertyCode)).flatMap((x) => x.m.onDay)),
        ...othersTakings(theirOthers, 'checkin'),
      ],
      vega: vega ? vega.filter((v) => mine(v.propertyCode)) : null,
      othersOut: theirOthers.filter((o) => o.shift === 'checkout' && o.bucket !== 'outside').length,
      othersIn: theirOthers.filter((o) => o.shift === 'checkin' && o.bucket !== 'outside').length,
    };
  });
}

/** What the ONE cash drawer should hold (UD4, operator 2026-10-18: « one cash
 *  drawer — same reception/staff »): the drawer's tenders (espèces, chèques,
 *  ANCV) of every block. The only desk figure that adds établissements — it
 *  reconciles a physical object, so it is shown as the drawer, never as takings,
 *  and nothing that a clôture reads comes from it. */
export function drawerCents(blocks: readonly { checkout: TakingsLine[]; checkin: TakingsLine[] }[]): number {
  return blocks.reduce(
    (sum, b) =>
      sum +
      takingsSummary([...b.checkout, ...b.checkin])
        .tiles.filter((t) => t.drawer)
        .reduce((s, t) => s + t.netCents, 0),
    0,
  );
}

/** What an « autre » was for, in the desk's words: the guest's stay against
 *  the sheet's day — « en séjour 23/09 → 26/09 », « arrive le 12/10 », « parti
 *  le 24/09 » — or nothing when Vega ties it to no booking (a bar sale). */
export function otherContext(o: OtherTaking, day: IsoDate): string | null {
  const a = o.arrivalDate;
  const d = o.departureDate;
  if (!a || !d) return null;
  if (a > day) return `arrive le ${dayMonth(a)}`;
  if (d < day) return `parti le ${dayMonth(d)}`;
  return `en séjour ${dayMonth(a)} → ${dayMonth(d)}`;
}
